import { Box } from "@chakra-ui/react";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { useMap } from "react-map-gl/maplibre";

import { RanaModal } from "@nens/ranalib";

import { RanaMap } from "../rana-map";

import { DraggableCaptureArea } from "./components/draggable-capture-area";
import { ExportSettingsPanel } from "./components/export-settings-panel";
import { EXPORT_MAP_ID } from "./consts";
import { useRanaMapExport } from "./hooks/use-rana-map-export";

interface RanaMapExportProps {
  onClose: () => void;
  mapId: string;
  initialFullscreen?: boolean;
}

export const RanaMapExport: React.FC<RanaMapExportProps> = ({
  onClose,
  initialFullscreen,
  mapId: sourceMapId,
}) => {
  const intl = useIntl();
  const { [sourceMapId]: sourceMap } = useMap();

  // Build initial view state and style from the source map — only computed once on mount
  const mapStyle = useMemo(() => sourceMap?.getMap().getStyle(), [sourceMap]);
  const initialViewState = useMemo(() => {
    if (!sourceMap) return {};
    const src = sourceMap.getMap();
    const { lng, lat } = src.getCenter();
    return {
      longitude: lng,
      latitude: lat,
      zoom: src.getZoom(),
      bearing: src.getBearing(),
      pitch: src.getPitch(),
    };
  }, [sourceMap]);

  const {
    captureArea,
    updateCaptureArea,
    updateCaptureAreaFromDelta,
    settings,
    updateFormat,
    updateOrientation,
    updateExportSize,
    updateCaptureAreaSize,
    updateFileName,
    isExporting,
    startExport,
  } = useRanaMapExport(onClose);

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={intl.formatMessage({
        id: "map.export_map",
        defaultMessage: "Export Map",
      })}
      w="full"
      h="full"
    >
      <Box
        position="relative"
        w="full"
        h="full"
        rounded="base"
        overflow="hidden"
        borderWidth="base"
        borderColor="brand.border"
      >
        <RanaMap
          mapId={EXPORT_MAP_ID}
          initialFullscreen={initialFullscreen}
          mapProps={{
            mapStyle,
            initialViewState,
          }}
          hideExport
        >
          <DraggableCaptureArea
            captureArea={captureArea}
            updateCaptureArea={updateCaptureArea}
            updateCaptureAreaFromDelta={updateCaptureAreaFromDelta}
          />
          <RanaMap.ControlsBottomLeft>
            <ExportSettingsPanel
              settings={settings}
              updateFormat={updateFormat}
              updateExportSize={updateExportSize}
              updateOrientation={updateOrientation}
              captureAreaWidth={Math.round(captureArea.width)}
              captureAreaHeight={Math.round(captureArea.height)}
              updateCaptureAreaSize={updateCaptureAreaSize}
              updateFileName={updateFileName}
              startExport={startExport}
              isExporting={isExporting}
            />
          </RanaMap.ControlsBottomLeft>
        </RanaMap>
      </Box>
    </RanaModal>
  );
};
